import {head} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {GetTireSetApiResponse, GetTireSetRequestApiResponse} from '@dms/api/core';
import i18n from '@dms/i18n';

const getSeasonLabel = (
  season: string | null | undefined,
  seasonOptions: {label: string; value: string}[] | undefined
): string | undefined => {
  if (!season) {
    return undefined;
  }

  const seasonOption = seasonOptions?.find((option) => option.value === season);

  return seasonOption?.label;
};

const getRequestTypeLabel = (
  tireSetRequest: GetTireSetRequestApiResponse | undefined
): string | undefined => {
  if (!tireSetRequest) {
    return undefined;
  }

  return tireSetRequest.homeBranchId === tireSetRequest.destinationBranchId
    ? i18n.t('page.tiresInventory.labels.local')
    : i18n.t('page.tiresInventory.labels.transit');
};

interface TireSetHeadlineOptions {
  manufacturerOptions: {label: string; value: string}[];
  tireWidthOptions: {label: string; value: string}[];
  aspectRatioOptions: {label: string; value: string}[];
  rimOptions: {label: string; value: string}[];
  seasonOptions: {label: string; value: string}[] | undefined;
}

export const getTireSetHeadline = (
  tireSet: GetTireSetApiResponse | null | undefined,
  tireSetRequest: GetTireSetRequestApiResponse | undefined,
  options: TireSetHeadlineOptions
): string => {
  if (!tireSet) {
    return '';
  }

  const firstWheel = head(tireSet.wheels ?? []);
  const wheelDetails = firstWheel?.wheelDetails;

  const tireSetNumber = tireSet.number;
  const detailParts: string[] = [];

  if (wheelDetails?.manufacturer) {
    const manufacturerLabel = options.manufacturerOptions.find(
      (option) => option.value === wheelDetails.manufacturer
    )?.label;

    if (manufacturerLabel) {
      detailParts.push(manufacturerLabel);
    }
  }

  if (wheelDetails?.title) {
    detailParts.push(wheelDetails.title);
  }

  const dimensionParts: string[] = [];

  if (wheelDetails?.tireWidth) {
    const tireWidthLabel = options.tireWidthOptions.find(
      (option) => option.value === wheelDetails.tireWidth
    )?.label;

    if (tireWidthLabel) {
      dimensionParts.push(tireWidthLabel);
    }
  }

  if (wheelDetails?.aspectRatio) {
    const aspectRatioLabel = options.aspectRatioOptions.find(
      (option) => option.value === wheelDetails.aspectRatio
    )?.label;

    if (aspectRatioLabel) {
      dimensionParts.push(aspectRatioLabel);
    }
  }

  if (dimensionParts.length > 0) {
    detailParts.push(dimensionParts.join('/'));
  }

  if (wheelDetails?.rim) {
    const rimLabel = options.rimOptions.find((option) => option.value === wheelDetails.rim)?.label;

    if (rimLabel) {
      detailParts.push(rimLabel);
    }
  }

  const seasonLabel = getSeasonLabel(wheelDetails?.season, options.seasonOptions);

  if (seasonLabel) {
    detailParts.push(seasonLabel);
  }

  const requestTypeLabel = getRequestTypeLabel(tireSetRequest);

  if (requestTypeLabel) {
    detailParts.push(requestTypeLabel);
  }

  const details = detailParts.filter(isNotNil).join(', ');

  if (!tireSetNumber) {
    return details;
  }

  return details ? `${tireSetNumber} - ${details}` : tireSetNumber;
};
